import { gql } from 'graphql-request';
import { graphqlClient } from '../client';
import { mergeGraphQLUpdate } from '@/store/actions/graphqlUpdateActions';

// Define the update company mutation
export const UPDATE_COMPANY_MUTATION = gql`
  mutation UpdateCompany($slug: String!, $input: CompanyUpdateInput!) {
    updateCompany(slug: $slug, input: $input) {
      id
      slug
      employees
      category
      logo_url
      description
      url
      scraper
    }
  }
`;

// Define input type
export interface CompanyUpdateInput {
  employees?: string;
  category?: string;
  logo_url?: string;
  description?: string;
  url?: string;
  scraper?: string;
}

// Define the response type for the mutation
export interface UpdateCompanyResponse {
  updateCompany: {
    id: string;
    slug: string;
    employees: string;
    category: string;
    logo_url: string | null;
    description: string | null;
    url: string | null;
    scraper: string | null;
  };
}

/**
 * Updates a company's details using GraphQL mutation
 * @param slug The company slug
 * @param input The update input object containing fields to update
 * @returns Promise with the updated company data
 */
export async function updateCompanyMutation(
  slug: string,
  input: CompanyUpdateInput
): Promise<UpdateCompanyResponse> {
  try {
    console.log(`🔄 [GraphQL] Updating company ${slug} with:`, input);
    
    // Special handling for scraper - convert "default" to empty string for storage
    if (input.scraper === 'default') {
      input.scraper = '';
    }
    
    // Execute the GraphQL mutation using our client wrapper
    const response = await graphqlClient.request<UpdateCompanyResponse>(
      UPDATE_COMPANY_MUTATION,
      {
        slug,
        input
      }
    );

    console.log('✅ [GraphQL] Company updated successfully:', response);

    // Merge the returned data into the Redux store so the UI reflects the change
    if (response && response.updateCompany) {
      mergeGraphQLUpdate(response.updateCompany);
    }

    return response;
  } catch (error: any) {
    // Provide more specific error messages based on the type of error
    console.error('❌ [GraphQL] Error updating company:', error);
    
    // Handle different types of errors with appropriate user-friendly messages
    if (error.message?.includes('network')) {
      throw new Error('Network error: Could not reach the GraphQL server');
    } else if (error.message?.includes('GraphQL')) {
      throw error; // Pass through GraphQL-specific errors
    } else {
      throw new Error(`Error updating company: ${error.message || 'Unknown error'}`);
    }
  }
}
